import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { getPneLocationRemoteId, PneAppointment } from '@nrwl/rx-models';
import {
  AccountLibraryStoreActions,
  AccountLibraryStoreSelectors,
  AccountLibraryStoreState,
} from '@nrwl/rx-state-account-library';
import { PneEmrService } from 'libs/rx-services/src/lib/api/pne-emr/pne-emr.service';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';

interface AppointmentConfig {
  appointment_id?: string | number | null;
  treatment_id?: string | number | null;
}

export interface AppointmentMetadata extends Record<string, unknown> {
  appointment_id?: string | number | null;
  appointmentId?: string | number | null;
  treatment_id?: string | number | null;
  treatmentId?: string | number | null;
  location_remote_id?: string | number | null;
}

@Injectable()
export class AppointmentContextService implements OnDestroy {
  private currentAppointment: PneAppointment | null = null;
  private currentAppointmentId: string | number | null = null;
  private currentTreatmentId: string | number | null = null;
  private resolvingAppointmentId: string | number | null = null;
  private treatmentResolution?: Subscription;

  constructor(
    private accountLibraryStore: Store<AccountLibraryStoreState.AccountLibraryState>,
    private pneEmrService: PneEmrService,
  ) {}

  public get appointment(): PneAppointment | null {
    return this.currentAppointment;
  }

  public get appointmentId(): string | number | null {
    return this.currentAppointmentId;
  }

  public get treatmentId(): string | number | null {
    return this.currentTreatmentId;
  }

  public setAppointmentId(appointmentId: string | number | null): void {
    this.currentAppointmentId = appointmentId;
  }

  public setTreatmentId(treatmentId: string | number | null): void {
    this.currentTreatmentId = treatmentId;
  }

  public setAppointmentValue(appointment: PneAppointment | null): void {
    this.currentAppointment = appointment;
  }

  public syncFromConfig(config: AppointmentConfig | null | undefined): void {
    const appointmentId = config?.appointment_id;
    const treatmentId = config?.treatment_id;

    if (this.hasValue(treatmentId)) {
      this.currentTreatmentId = treatmentId;
    }

    if (!this.hasValue(appointmentId)) {
      return;
    }

    const appointment = {
      AppointmentId: appointmentId,
      ...(this.hasValue(treatmentId) ? { TreatmentId: treatmentId } : {}),
    } as PneAppointment;

    this.setAppointment(appointment, true);
    this.ensureTreatmentId();
  }

  public hydrateFromStore(): void {
    try {
      this.accountLibraryStore
        .select(AccountLibraryStoreSelectors.getAppointment)
        .pipe(
          take(1),
          catchError(() => of(null)),
        )
        .subscribe((appointment) => {
          if (appointment) {
            this.setAppointment(appointment);
          }
          this.ensureTreatmentId();
        });
    } catch {
      console.warn('PNE Appointment ID skipped: Store not available');
    }
  }

  public hydrateFromMetadata(
    metadata: AppointmentMetadata | null | undefined,
    isPne?: boolean,
  ): void {
    if (!metadata || !isPne) {
      return;
    }

    const appointmentId = metadata.appointment_id ?? metadata.appointmentId;
    if (!this.hasValue(appointmentId)) {
      return;
    }

    const treatmentId = metadata.treatment_id ?? metadata.treatmentId;
    const locationRemoteId = metadata.location_remote_id;
    const appointment = {
      AppointmentId: appointmentId,
      ...(this.hasValue(locationRemoteId) ? { Location_ID: locationRemoteId } : {}),
      ...(this.hasValue(treatmentId) ? { TreatmentId: treatmentId } : {}),
    } as PneAppointment;

    this.setAppointment(appointment, true);
    this.ensureTreatmentId();
  }

  public enrichMetadata(
    metadata: AppointmentMetadata,
    existingFormTreatmentId?: string | number | null,
  ): AppointmentMetadata {
    const result = { ...metadata };

    const appointmentId = this.appointment?.AppointmentId ?? this.appointmentId;
    if (this.hasValue(appointmentId)) {
      result.appointment_id = appointmentId;
    }

    const locationRemoteId = getPneLocationRemoteId(this.appointment);
    if (locationRemoteId !== null) {
      result.location_remote_id = locationRemoteId;
    }

    const treatmentId =
      existingFormTreatmentId ??
      this.appointment?.TreatmentId ??
      this.treatmentId ??
      result.treatment_id;
    if (this.hasValue(treatmentId)) {
      result.treatment_id = treatmentId;
    }

    return result;
  }

  public ngOnDestroy(): void {
    this.treatmentResolution?.unsubscribe();
  }

  private setAppointment(appointment: PneAppointment, dispatch = false): void {
    const appointmentChanged = this.currentAppointment?.AppointmentId !== appointment.AppointmentId;

    this.currentAppointment = appointment;
    if (appointmentChanged) {
      this.currentAppointmentId = appointment.AppointmentId ?? null;
      this.currentTreatmentId = appointment.TreatmentId ?? null;
    } else if (this.hasValue(appointment.TreatmentId) && !this.hasValue(this.currentTreatmentId)) {
      this.currentTreatmentId = appointment.TreatmentId;
    }

    if (dispatch) {
      this.dispatchAppointment(appointment);
    }
  }

  private ensureTreatmentId(): void {
    const appointmentId = this.appointment?.AppointmentId ?? this.appointmentId;
    const treatmentId = this.appointment?.TreatmentId ?? this.treatmentId;

    if (!this.hasValue(appointmentId) || this.hasValue(treatmentId)) {
      this.cancelTreatmentResolution();
      return;
    }

    if (this.resolvingAppointmentId === appointmentId) {
      return;
    }

    this.cancelTreatmentResolution();
    this.resolvingAppointmentId = appointmentId;
    this.treatmentResolution = this.pneEmrService
      .getTreatmentByAppointment(appointmentId)
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => {
          if (this.resolvingAppointmentId === appointmentId) {
            this.resolvingAppointmentId = null;
          }
        }),
      )
      .subscribe((resolvedTreatmentId) => {
        const currentAppointmentId =
          this.currentAppointment?.AppointmentId ?? this.currentAppointmentId;
        if (currentAppointmentId !== appointmentId || !this.hasValue(resolvedTreatmentId)) {
          return;
        }

        this.currentTreatmentId = resolvedTreatmentId;
        if (!this.currentAppointment) {
          return;
        }

        const appointment = {
          ...this.currentAppointment,
          TreatmentId: resolvedTreatmentId,
        } as PneAppointment;
        this.currentAppointment = appointment;
        this.dispatchAppointment(appointment);
      });
  }

  private cancelTreatmentResolution(): void {
    this.treatmentResolution?.unsubscribe();
    this.treatmentResolution = undefined;
    this.resolvingAppointmentId = null;
  }

  private dispatchAppointment(appointment: PneAppointment): void {
    this.accountLibraryStore.dispatch(
      new AccountLibraryStoreActions.SetAppointment({ appointment }),
    );
  }

  private hasValue(value: unknown): value is string | number {
    return value !== null && value !== undefined && value !== '';
  }
}
